import React, { useContext, useMemo } from "react";
import { FieldPath, FieldValues } from "react-hook-form";
import { GroupBase } from "react-select";
import {
  ReactSelectSyncProps,
  ReactSelectAsyncProps,
  ReactSelectController,
  ReactSelectControllerProps,
} from "@pushpress/shared-components";
import { sortByName } from "../../../utils/sortUtils";
import { AppContext } from "../../../context/app";
import {
  LocationControllerOptionFragment,
  useGetLocationsQuery,
} from "./LocationSelect-generated.hooks";

type LocationControllerProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  Option extends LocationControllerOptionFragment,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
> = ReactSelectControllerProps<TFieldValues, TName, Option, IsMulti, Group>;

function LocationController<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  Option extends LocationControllerOptionFragment,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  props: LocationControllerProps<TFieldValues, TName, Option, IsMulti, Group> &
    (ReactSelectSyncProps | ReactSelectAsyncProps),
) {
  const { ...other } = props;

  const app = useContext(AppContext);

  const { data } = useGetLocationsQuery({
    variables: {
      getLocationsInput: {
        clientUuid: app.client.uuid,
      },
    },
  });

  const getOptionLabel = (option: LocationControllerOptionFragment) =>
    option.name;

  const getOptionValue = (option: LocationControllerOptionFragment) =>
    option.uuid;

  const options = useMemo(() => {
    if (!data) {
      return [];
    }
    return [...data?.getLocations].sort(sortByName) as any;
  }, [data]);

  return (
    <ReactSelectController
      menuPortalTarget={document.body}
      {...other}
      options={options}
      getOptionLabel={getOptionLabel}
      getOptionValue={getOptionValue}
    />
  );
}

export default LocationController;
